/**
 * Pointer-driven perspective for hologram panels.
 *
 * One delegated `pointermove` on the shell root rather than a listener per
 * panel: screens rebuild their `innerHTML` constantly, which would orphan any
 * per-element listener. The helper only writes four custom properties on the
 * hovered panel (`--rx`, `--ry`, `--mx`, `--my`); app.css owns what they mean,
 * so the 3D itself stays on the panel and never on an ancestor (an ancestor
 * transform would become the containing block for every fixed modal/toast).
 *
 * Touch gets nothing from this on purpose — hover is decoration here, never a
 * required affordance — so it is gated to a fine, hoverable pointer, and it
 * stands down for both the in-app Reduce-motion toggle and the OS preference.
 */

const TARGET = '.glass-panel, .card-plate, .mars-horizon.rounded-lg, [data-holo-tilt]';
/** Anything the player types into must stay flat: a rotated ancestor mis-places the iOS caret and softens text. */
const FORM_CONTROLS = 'input, select, textarea, canvas';
const VARS = ['--rx', '--ry', '--mx', '--my'] as const;

/** Degrees of tilt at the panel edge for a ~420px panel; larger panels tilt less so they never swing. */
const MAX_TILT_DEG = 9;
const MIN_TILT_DEG = 0.8;
const REFERENCE_SIZE_PX = 420;

export interface TiltValues {
  rx: number;
  ry: number;
  /** Pointer position within the panel, 0-100 — drives the specular highlight. */
  mx: number;
  my: number;
}

/** Pure: maps a pointer position inside `rect` to tilt angles (deg) and highlight coordinates (%). */
export function computeTilt(rect: { left: number; top: number; width: number; height: number }, clientX: number, clientY: number): TiltValues {
  const w = Math.max(rect.width, 1);
  const h = Math.max(rect.height, 1);
  const px = Math.min(1, Math.max(0, (clientX - rect.left) / w));
  const py = Math.min(1, Math.max(0, (clientY - rect.top) / h));
  const max = Math.max(MIN_TILT_DEG, MAX_TILT_DEG * Math.min(1, REFERENCE_SIZE_PX / Math.max(w, h)));
  return {
    // Pointer near the top edge pushes the top away (positive rotateX); near the right edge turns the right side away.
    rx: round((py - 0.5) * -2 * max),
    ry: round((px - 0.5) * 2 * max),
    mx: round(px * 100),
    my: round(py * 100),
  };
}

function round(n: number): number {
  return Math.round(n * 100) / 100 + 0; // + 0 folds -0 into 0
}

function motionAllowed(): boolean {
  if (document.documentElement.dataset.reduceMotion === 'true') return false;
  if (typeof window.matchMedia !== 'function') return false;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
  return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
}

function isFlat(el: HTMLElement): boolean {
  try {
    return el.matches(`:has(${FORM_CONTROLS})`);
  } catch {
    // No :has support — fail safe to flat rather than risk tilting a form.
    return true;
  }
}

function release(el: HTMLElement): void {
  for (const v of VARS) el.style.removeProperty(v);
  delete el.dataset.holoLive;
}

/** Wires pointer tilt for every `.glass-panel` / `[data-holo-tilt]` under `root`. Returns its teardown. */
export function mountHoloTilt(root: HTMLElement): () => void {
  let active: HTMLElement | null = null;
  let frame = 0;
  let pending: { el: HTMLElement; x: number; y: number } | null = null;

  function apply(): void {
    frame = 0;
    if (!pending) return;
    const { el, x, y } = pending;
    pending = null;
    const { rx, ry, mx, my } = computeTilt(el.getBoundingClientRect(), x, y);
    el.style.setProperty('--rx', `${rx}deg`);
    el.style.setProperty('--ry', `${ry}deg`);
    el.style.setProperty('--mx', `${mx}%`);
    el.style.setProperty('--my', `${my}%`);
    el.dataset.holoLive = '';
  }

  function onMove(e: Event): void {
    const pe = e as PointerEvent;
    if (pe.pointerType === 'touch' || pe.pointerType === 'pen') return;
    const target = (e.target as Element | null)?.closest?.(TARGET) as HTMLElement | null;
    if (target !== active && active) {
      release(active);
      active = null;
    }
    if (!target || !root.contains(target) || !motionAllowed() || isFlat(target)) return;
    active = target;
    pending = { el: target, x: pe.clientX, y: pe.clientY };
    if (!frame) frame = requestAnimationFrame(apply);
  }

  function onLeave(): void {
    pending = null;
    if (active) release(active);
    active = null;
  }

  root.addEventListener('pointermove', onMove);
  root.addEventListener('pointerleave', onLeave);
  return () => {
    root.removeEventListener('pointermove', onMove);
    root.removeEventListener('pointerleave', onLeave);
    if (frame) cancelAnimationFrame(frame);
    onLeave();
  };
}
